import type { AnalysisPageSearchQueryStates } from '@/features/analysis/_hooks/search-query-states';
import type { graphTypeEnum } from '@/features/analysis/_models/project';
import { isPS } from '@/utils';
import type { FieldPath } from 'react-hook-form';
import type z from 'zod';
import {
  type AnalysisSearchFormType,
  initialValueOfAnalysisSearchForm,
} from '../_models/analysis-search';
import type { ProjectSearchFormType } from '../_models/projects-search';

const getIsPsParams = ({
  nestedExpenditureBudgetPurposeIds,
  nestedPolicyIds,
  nestedOrganizationIds,
  nestedOtherResponsibleOrganizationIds,
}: AnalysisSearchFormType) => {
  if (!isPS()) {
    return {
      expenditureBudgetPurposeIds: null,
      policyIds: null,
      organizationIds: null,
      otherResponsibleOrganizationIds: null,
    };
  }

  return {
    expenditureBudgetPurposeIds: nestedExpenditureBudgetPurposeIds?.length
      ? nestedExpenditureBudgetPurposeIds.flatMap((item) => item.at(-1) ?? [])
      : null,
    policyIds: nestedPolicyIds?.length
      ? nestedPolicyIds.flatMap((item) => item.at(-1) ?? [])
      : null,
    organizationIds: nestedOrganizationIds?.length
      ? nestedOrganizationIds.flatMap((item) => item.at(-1) ?? [])
      : null,
    otherResponsibleOrganizationIds:
      nestedOtherResponsibleOrganizationIds?.length
        ? nestedOtherResponsibleOrganizationIds.flatMap(
            (item) => item.at(-1) ?? [],
          )
        : null,
  };
};

export const convertInputAnalysisValueToRequestParams = ({
  values,
  newCascadeId,
  graphType,
  order,
  currentFiscalYear,
}: {
  values: AnalysisSearchFormType;
  newCascadeId: string | undefined | null;
  order: string | null;
  graphType: z.infer<typeof graphTypeEnum>;
  currentFiscalYear: number;
}): AnalysisPageSearchQueryStates => {
  const { period, fiscalYearRange, analysisTarget } = values;

  const isExpenditure = analysisTarget === 'expenditure';

  return {
    page: 1,
    pageSize: 30,
    analysisTarget,
    graphType,
    order,
    fiscalYearRange: fiscalYearRange
      ? [fiscalYearRange?.[0]?.year() ?? 0, fiscalYearRange?.[1]?.year() ?? 0]
      : initialValueOfAnalysisSearchForm(currentFiscalYear).fiscalYearRange,
    q: values.q ?? null,
    isSearchTargetProjectName: values.q
      ? values.isSearchTargetProjectName
      : (null as any),
    cascader: newCascadeId ?? null,
    corporateNumber: values.corporateNumber ?? null,
    categories: values.categories?.length ? values.categories : null,
    implementationMethods: values.implementationMethods?.length
      ? values.implementationMethods
      : null,
    accountingType: isExpenditure ? values.accountingType : null,
    accountingTypeIds:
      isExpenditure && values.accountingTypeIds?.length
        ? values.accountingTypeIds
        : null,
    projectNumbers: values.projectNumbers ?? null,
    startYear: period?.[0]?.year() ?? null,
    endYear: period?.[1]?.year() ?? null,
    aggregationType: values.aggregationType ?? null,
    aggregationTargetType: values.aggregationTargetType ?? null,
    analysisAxis: !values.analysisAxis ? 'year' : values.analysisAxis,
    ...getIsPsParams(values),
  };
};

type FiledName = FieldPath<ProjectSearchFormType>;
export type AmountSearchConditionFieldName<T extends FiledName[number]> =
  `formulaConditions.${number}.${T}`;

/**
 * 増減条件の入力フィールドが非活性かどうかを判定する
 * @param baseAmountYear 基準年度
 * @param targetAmountYear 対象年度
 * @returns 非活性の場合はtrue
 */
export const isTrendFieldDisabled = (
  baseAmountYear: string | undefined | null,
  targetAmountYear: string | undefined | null,
): boolean => {
  return !(baseAmountYear && targetAmountYear);
};
